import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, RefreshCw, Filter, Trash2, Search, X, Tags } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatCurrency } from '@/lib/format';
import { toast } from '@/stores/toast';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/SortableTh';
import { EmptyState } from '@/components/EmptyState';
import { useLabel } from '@/lib/labels';
import type { MenuCategory } from './MenuCategoriesPage';

type Menu = {
  menu_id: string;
  store_id: string;
  menu_code: string;
  menu_name: string;
  menu_category_id: string | null;
  unit_price: number;
  tax_rate: number;
  is_main_menu: boolean;
  effective_from: string;
  effective_to: string | null;
  active_flg: boolean;
};

type Store = { store_id: string; store_code: string; store_name: string };

// カテゴリラベルは API から取得（後方互換のため未取得時のフォールバックのみ最低限保持）
const categoryLabelsFallback: Record<string, string> = {
  'MC-10': 'ラーメン',
  'MC-20': 'ライス',
  'MC-30': 'トッピング',
  'MC-40': 'ドリンク',
};

/** yyyy-mm-dd を UTC 経由せず「文字列で1日前」に変換（タイムゾーン非依存） */
const subOneDay = (yyyymmdd: string): string => {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  if (!y || !m || !d) return yyyymmdd;
  // ローカル Date の setDate を使う（UTC変換を避ける）
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - 1);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

export function MenusPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const { data: menus = [], isLoading } = useQuery<Menu[]>({
    queryKey: ['menus'],
    queryFn: () => apiFetch<Menu[]>('/menus'),
  });
  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });
  const { data: categories = [] } = useQuery<MenuCategory[]>({
    queryKey: ['menu-categories'],
    queryFn: () => apiFetch<MenuCategory[]>('/menu-categories'),
  });
  // API 取得結果でカテゴリラベルを構築、フォールバックとマージ
  const categoryLabels = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = { ...categoryLabelsFallback };
    for (const c of categories) map[c.menu_category_id] = c.category_name;
    return map;
  }, [categories]);

  const [filterStore, setFilterStore] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  // 履歴を確認するユースケースが多いため、デフォルトで全履歴表示（過去分も見せる）
  const [filterActive, setFilterActive] = useState(false);
  const [filterMainOnly, setFilterMainOnly] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [editing, setEditing] = useState<Partial<Menu> | null>(null);
  const [revising, setRevising] = useState<Menu | null>(null);
  const [deleting, setDeleting] = useState<Menu | null>(null);
  // 一括変更用
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkCategoryId, setBulkCategoryId] = useState<string>('');
  const toggleSelected = (id: string) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const storeName = (id: string) => stores.find((s) => s.store_id === id)?.store_name ?? id;
  const storeCode = (id: string) => stores.find((s) => s.store_id === id)?.store_code ?? '';

  const filtered = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const q = searchText.trim().toLowerCase();
    return menus.filter((m) => {
      if (filterStore && m.store_id !== filterStore) return false;
      if (filterCategory && m.menu_category_id !== filterCategory) return false;
      if (filterMainOnly && !m.is_main_menu) return false;
      if (filterActive && !(m.active_flg && (m.effective_to == null || m.effective_to >= today))) return false;
      if (q && !(m.menu_code.toLowerCase().includes(q) || m.menu_name.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [menus, filterStore, filterCategory, filterActive, filterMainOnly, searchText]);

  const sortAccessors = {
    store_code: (m: Menu) => storeCode(m.store_id),
    menu_code: (m: Menu) => {
      const n = Number(m.menu_code);
      return Number.isFinite(n) ? n : m.menu_code;
    },
    menu_name: (m: Menu) => m.menu_name,
    category: (m: Menu) => (m.menu_category_id ? categoryLabels[m.menu_category_id] ?? m.menu_category_id : ''),
    unit_price: (m: Menu) => m.unit_price,
    is_main_menu: (m: Menu) => m.is_main_menu,
    effective_from: (m: Menu) => m.effective_from,
    effective_to: (m: Menu) => m.effective_to ?? '9999-12-31',
  } as const;

  const { sortKey, sortDir, toggleSort, sortedRows } = useTableSort<Menu>(filtered, {
    defaultKey: 'store_code',
    defaultDir: 'asc',
    accessors: sortAccessors as unknown as Record<string, (m: Menu) => unknown>,
  });

  const hasAnyFilter = Boolean(filterStore || filterCategory || filterMainOnly || searchText || !filterActive);
  const clearFilters = () => {
    setFilterStore('');
    setFilterCategory('');
    setFilterMainOnly(false);
    setSearchText('');
    setFilterActive(false); // 全履歴表示に戻す
  };

  const createMut = useMutation({
    mutationFn: (m: Partial<Menu>) => apiFetch('/menus', {
      method: 'POST',
      body: JSON.stringify(m),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['menus'] });
      setEditing(null);
      toast.success('メニューを追加しました');
    },
  });

  const updateMut = useMutation({
    mutationFn: (m: Partial<Menu>) => {
      // 変更禁止フィールド（store_id / menu_code / effective_from / effective_to）は PATCH ペイロードから除外
      // バックエンドでも拒否されるが、無駄な400を防ぐためフロント側でも omit する
      const { store_id: _sid, menu_code: _mc, effective_from: _ef, effective_to: _et, ...allowed } = m;
      return apiFetch(`/menus/${m.menu_id}`, {
        method: 'PATCH',
        body: JSON.stringify(allowed),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['menus'] });
      setEditing(null);
      toast.success('メニューを更新しました');
    },
  });

  const reviseMut = useMutation({
    mutationFn: (payload: any) => apiFetch(`/menus/${payload.menu_id}/revise`, {
      method: 'POST',
      body: JSON.stringify(payload.body),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['menus'] });
      setRevising(null);
      toast.success('メニュー価格改定を反映しました');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (menu: Menu) =>
      apiFetch<{ deleted_menu_id: string; restored_prior_menu_id: string | null }>(
        `/menus/${menu.menu_id}`,
        { method: 'DELETE' },
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['menus'] });
      setDeleting(null);
      if (data.restored_prior_menu_id) {
        toast.success('メニュー履歴を削除しました（直前の履歴の有効期間を「無期限」に自動復元しました）');
      } else {
        toast.success('メニュー履歴を削除しました');
      }
    },
  });

  // フィルタ変更で非表示になった選択を落とす
  //   → 一括操作は「見えている範囲」で完結し、hidden な行を勝手にいじらない
  useEffect(() => {
    const visibleIds = new Set(filtered.map((m) => m.menu_id));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (visibleIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [filtered]);

  const bulkUpdateCatMut = useMutation({
    mutationFn: (payload: { menu_ids: string[]; menu_category_id: string | null }) =>
      apiFetch<{ updated: number }>('/menus/bulk-update-category', {
        method: 'POST', body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['menus'] });
      setSelectedIds(new Set());
      setBulkCategoryId('');
      toast.success(`${data.updated} 件のメニューのカテゴリを変更しました`);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-h1">{t('entity.menus', 'メニューマスタ')}</h1>
        <div className="flex items-center gap-2">
          <Link to="/masters/menu-categories"
            className="inline-flex items-center gap-1 px-3 py-2 rounded border border-neutral-line text-sm text-neutral-text hover:bg-neutral-panel"
          >
            <Tags size={14} /> カテゴリマスタ
          </Link>
          <button
            onClick={() => setEditing({
              store_id: stores[0]?.store_id ?? '', menu_code: '', menu_name: '',
              menu_category_id: 'MC-10', unit_price: 1000, tax_rate: 0.10,
              is_main_menu: true, active_flg: true,
              effective_from: new Date().toISOString().slice(0, 10),
            })}
            className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover"
          >
            <Plus size={16} /> 新規登録
          </button>
        </div>
      </div>

      {/* フィルタバー */}
      <div className="border border-neutral-line rounded-lg bg-white p-3 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-neutral-sub" />
        <select value={filterStore} onChange={(e) => setFilterStore(e.target.value)}
          className="px-2 py-1 border border-neutral-line rounded text-sm">
          <option value="">全店舗</option>
          {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
        </select>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
          className="px-2 py-1 border border-neutral-line rounded text-sm">
          <option value="">全カテゴリ</option>
          {categories.map((c) => <option key={c.menu_category_id} value={c.menu_category_id}>{c.category_name}</option>)}
        </select>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-sub pointer-events-none" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="コード・名称で検索"
            className="pl-6 pr-2 py-1 border border-neutral-line rounded text-sm w-52"
          />
        </div>
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={filterActive} onChange={(e) => setFilterActive(e.target.checked)} />
          有効なメニューのみ
        </label>
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={filterMainOnly} onChange={(e) => setFilterMainOnly(e.target.checked)} />
          主要メニューのみ
        </label>
        <div className="flex-1" />
        <div className="text-xs text-neutral-sub tabular-nums whitespace-nowrap">
          {sortedRows.length} 件表示中（全 {menus.length} 件）
        </div>
        {hasAnyFilter && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 text-xs text-neutral-sub hover:text-brand-red"
          >
            <X size={12} /> 絞込みをクリア
          </button>
        )}
      </div>

      {/* 一括変更バー（1件以上選択時のみ表示、ヘッダ h-14 の下にスティッキー） */}
      {selectedIds.size > 0 && (
        <div className="sticky top-14 z-20 border border-brand-red/40 rounded-lg bg-brand-red/5 backdrop-blur-sm p-3 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-brand-red">
            {selectedIds.size} 件を選択中
          </span>
          <div className="flex items-center gap-2">
            <label htmlFor="bulk-category-select" className="text-xs text-neutral-sub">カテゴリ一括変更：</label>
            <select id="bulk-category-select"
              value={bulkCategoryId} onChange={(e) => setBulkCategoryId(e.target.value)}
              className="px-2 py-1 border border-neutral-line rounded text-sm bg-white">
              <option value="">— 選択 —</option>
              <optgroup label="カテゴリ">
                {categories.map((c) => (
                  <option key={c.menu_category_id} value={c.menu_category_id}>{c.category_name}</option>
                ))}
              </optgroup>
              <optgroup label="その他">
                <option value="__null__">未分類にする</option>
              </optgroup>
            </select>
            <button
              type="button"
              disabled={!bulkCategoryId || bulkUpdateCatMut.isPending}
              onClick={() => {
                if (!bulkCategoryId) return;
                bulkUpdateCatMut.mutate({
                  menu_ids: Array.from(selectedIds),
                  menu_category_id: bulkCategoryId === '__null__' ? null : bulkCategoryId,
                });
              }}
              className="px-3 py-1 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50"
            >
              {bulkUpdateCatMut.isPending ? '変更中…' : '一括変更を実行'}
            </button>
          </div>
          <div className="flex-1" />
          <button type="button" onClick={() => setSelectedIds(new Set())}
            className="text-xs text-neutral-sub hover:text-brand-red">
            選択をクリア
          </button>
        </div>
      )}

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th scope="col" className="sticky left-0 z-20 bg-neutral-panel w-10 px-2 py-2">
                  <input
                    type="checkbox"
                    aria-label="表示中の全メニューを選択"
                    checked={sortedRows.length > 0 && sortedRows.every((m) => selectedIds.has(m.menu_id))}
                    ref={(el) => {
                      if (!el) return;
                      const some = sortedRows.some((m) => selectedIds.has(m.menu_id));
                      const all = sortedRows.length > 0 && sortedRows.every((m) => selectedIds.has(m.menu_id));
                      el.indeterminate = some && !all;
                    }}
                    onChange={(e) => {
                      // 表示中の行のみに対して全選択/全解除（フィルタ外の選択は保持しない設計）
                      const visibleIds = new Set(sortedRows.map((m) => m.menu_id));
                      if (e.target.checked) {
                        setSelectedIds((prev) => new Set([...prev, ...visibleIds]));
                      } else {
                        setSelectedIds((prev) => new Set([...prev].filter((id) => !visibleIds.has(id))));
                      }
                    }}
                  />
                </th>
                <SortableTh sortKey="store_code" currentKey={sortKey} dir={sortDir} onClick={toggleSort}
                  className="sticky left-10 z-20 bg-neutral-panel shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">{t('field.menus.store_id', '店舗')}</SortableTh>
                <SortableTh sortKey="menu_code" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>{t('field.menus.menu_code', 'コード')}</SortableTh>
                <SortableTh sortKey="menu_name" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>{t('field.menus.menu_name', 'メニュー名')}</SortableTh>
                <SortableTh sortKey="category" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>{t('field.menus.menu_category_id', 'カテゴリ')}</SortableTh>
                <SortableTh sortKey="unit_price" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">{t('field.menus.unit_price', '単価')}(税込)</SortableTh>
                <SortableTh sortKey="is_main_menu" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="center">{t('field.menus.is_main_menu', '主要')}</SortableTh>
                <SortableTh sortKey="effective_from" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>有効期間</SortableTh>
                <th scope="col" className="w-32 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((m) => {
                const checked = selectedIds.has(m.menu_id);
                // 選択時は行全体をハイライトし、sticky列にも同じ背景を適用
                const rowBg = checked ? 'bg-warning-row' : 'bg-white hover:bg-neutral-panel';
                const stickyBg = checked ? 'bg-warning-row' : 'bg-white group-hover:bg-neutral-panel';
                return (
                  <tr key={m.menu_id} className={`border-t border-neutral-line group ${rowBg}`}>
                    <td className={`sticky left-0 z-10 w-10 px-2 py-2 ${stickyBg}`}>
                      <input type="checkbox" checked={checked}
                        aria-label={`${m.menu_name}を選択`}
                        onChange={() => toggleSelected(m.menu_id)} />
                    </td>
                    <td className={`sticky left-10 z-10 px-3 py-2 whitespace-nowrap shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] ${stickyBg}`}>
                      {storeName(m.store_id)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{m.menu_code}</td>
                    <td className="px-3 py-2">{m.menu_name}</td>
                    <td className="px-3 py-2">{m.menu_category_id ? categoryLabels[m.menu_category_id] ?? m.menu_category_id : '−'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(m.unit_price)}</td>
                    <td className="px-3 py-2 text-center">{m.is_main_menu ? '◯' : ''}</td>
                    <td className="px-3 py-2 text-xs text-neutral-sub tabular-nums whitespace-nowrap">
                      {m.effective_from}〜{m.effective_to ?? '（無期限）'}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                      <button onClick={() => setRevising(m)} type="button"
                        aria-label="価格改定" title="価格改定（新しい履歴を作成、旧履歴は自動でクローズ）"
                        className="p-1 text-brand-orange hover:text-brand-red">
                        <RefreshCw size={14} />
                      </button>
                      <button onClick={() => setEditing(m)} type="button"
                        aria-label="編集" title="編集（現在の内容のみ・有効期間の変更不可）"
                        className="p-1 text-neutral-sub hover:text-brand-red">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => setDeleting(m)} type="button"
                        aria-label="この履歴を削除" title="この履歴を削除（前後の履歴は自動で調整）"
                        className="p-1 text-neutral-sub hover:text-brand-red">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sortedRows.length === 0 && (
            <EmptyState
              message={hasAnyFilter ? 'フィルタ条件に一致するメニューがありません' : 'メニューが登録されていません'}
              description={hasAnyFilter ? '絞込条件を変更するか、クリアして再表示してください' : '「＋ 新規登録」からメニューを追加してください'}
              actionLabel={hasAnyFilter ? '絞込みをクリア' : undefined}
              onAction={hasAnyFilter ? clearFilters : undefined}
            />
          )}
        </div>
      )}

      {/* 通常編集モーダル */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditing(null)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              if (editing.menu_id) updateMut.mutate(editing);
              else createMut.mutate(editing);
            }}
            className="bg-white rounded-lg p-6 w-[560px] max-w-full max-h-[90vh] overflow-y-auto space-y-3"
          >
            <h2 className="text-h3 mb-1">{editing.menu_id ? 'メニュー編集（現在の内容のみ）' : 'メニュー新規登録'}</h2>
            {editing.menu_id && (
              <p className="text-xs text-neutral-sub mb-2">
                ※ 過去に遡及する変更を避けたい場合は「価格改定」機能をご利用ください
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.store_id', '店舗')}*</label>
                <select required value={editing.store_id ?? ''}
                  disabled={!!editing.menu_id}
                  onChange={(e) => setEditing({ ...editing, store_id: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring disabled:bg-neutral-panel disabled:text-neutral-sub disabled:cursor-not-allowed">
                  <option value="">— 選択 —</option>
                  {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.menu_code', 'メニューコード')}*</label>
                <input required value={editing.menu_code ?? ''}
                  disabled={!!editing.menu_id}
                  pattern="[A-Za-z0-9_-]+"
                  onChange={(e) => setEditing({ ...editing, menu_code: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring disabled:bg-neutral-panel disabled:text-neutral-sub disabled:cursor-not-allowed" />
                {!editing.menu_id && <p className="text-xs text-neutral-sub mt-1">半角英数字・ハイフン・アンダースコアのみ</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.menu_name', 'メニュー名')}*</label>
              <input required value={editing.menu_name ?? ''}
                onChange={(e) => setEditing({ ...editing, menu_name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.menu_category_id', 'カテゴリ')}</label>
                <select value={editing.menu_category_id ?? ''}
                  onChange={(e) => setEditing({ ...editing, menu_category_id: e.target.value || null })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                  {categories.map((c) => <option key={c.menu_category_id} value={c.menu_category_id}>{c.category_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.unit_price', '単価')}(税込)*</label>
                <input required type="number" min={1} value={editing.unit_price ?? ''}
                  onChange={(e) => setEditing({ ...editing, unit_price: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.tax_rate', '税率')}</label>
                <input type="number" step="0.01" min={0} max={1} value={editing.tax_rate ?? 0.10}
                  onChange={(e) => setEditing({ ...editing, tax_rate: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_main_menu ?? false}
                  onChange={(e) => setEditing({ ...editing, is_main_menu: e.target.checked })} />
                {t('field.menus.is_main_menu', '主要メニュー')}（来客数計上対象）
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active_flg ?? true}
                  onChange={(e) => setEditing({ ...editing, active_flg: e.target.checked })} />
                {t('field.menus.active_flg', '有効')}
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.effective_from', '有効開始日')}{!editing.menu_id && '*'}</label>
                <input required={!editing.menu_id} type="date"
                  value={editing.effective_from ?? ''}
                  disabled={!!editing.menu_id}
                  onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring disabled:bg-neutral-panel disabled:text-neutral-sub disabled:cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.effective_to', '有効終了日')}</label>
                <input type="date"
                  value={editing.effective_to ?? ''}
                  disabled={!!editing.menu_id}
                  onChange={(e) => setEditing({ ...editing, effective_to: e.target.value || null })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring disabled:bg-neutral-panel disabled:text-neutral-sub disabled:cursor-not-allowed" />
              </div>
            </div>
            {editing.menu_id && (
              <div className="text-xs text-warning-text bg-warning-bg border border-warning-border rounded px-2 py-1.5">
                💡 有効期間はここでは変更できません。日付を変更したい場合は編集をキャンセルして「🔄 改定」ボタンをご利用ください（履歴が正しく残り、空白期間もできません）
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)}
                className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
              <button type="submit" disabled={createMut.isPending || updateMut.isPending}
                className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
                {createMut.isPending || updateMut.isPending ? '保存中…' : '保存'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 価格改定モーダル */}
      {revising && (
        <ReviseModal
          menu={revising}
          storeName={storeName(revising.store_id)}
          categories={categories}
          onClose={() => setRevising(null)}
          onSubmit={(body) => reviseMut.mutate({ menu_id: revising.menu_id, body })}
          isPending={reviseMut.isPending}
        />
      )}

      {/* 削除確認モーダル */}
      {deleting && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDeleting(null)}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg p-6 w-[520px] max-w-full space-y-3">
            <h2 className="text-h3 flex items-center gap-2 text-warning-text">
              <Trash2 size={18} /> メニュー履歴の削除確認
            </h2>
            <div className="bg-neutral-panel rounded p-3 text-sm space-y-0.5">
              <div><span className="text-neutral-sub">店舗：</span>{storeName(deleting.store_id)}</div>
              <div><span className="text-neutral-sub">コード：</span>{deleting.menu_code}</div>
              <div><span className="text-neutral-sub">名称：</span>{deleting.menu_name}</div>
              <div><span className="text-neutral-sub">単価：</span>{formatCurrency(deleting.unit_price)}</div>
              <div><span className="text-neutral-sub">有効期間：</span>{deleting.effective_from}〜{deleting.effective_to ?? '（無期限）'}</div>
            </div>
            <div className="text-xs text-neutral-sub space-y-1">
              <div>• 売上明細が紐付いている場合は削除できません（メニュー編集で「有効」チェックを外して無効化してください）</div>
              <div>• 直前の履歴の有効終了日が「削除する履歴の有効開始日の前日」だった場合、自動的に「無期限」に復元します</div>
              <div className="text-warning-text font-medium mt-1">この操作は取り消せません。よろしいですか？</div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setDeleting(null)}
                className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
              <button
                onClick={() => deleteMut.mutate(deleting)}
                disabled={deleteMut.isPending}
                className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
                {deleteMut.isPending ? '削除中…' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// 価格改定モーダル
// ============================================================================
function ReviseModal({
  menu, storeName, categories, onClose, onSubmit, isPending,
}: {
  menu: Menu;
  storeName: string;
  categories: MenuCategory[];
  onClose: () => void;
  onSubmit: (body: any) => void;
  isPending: boolean;
}) {
  const t = useLabel();
  const [form, setForm] = useState({
    effective_from: new Date().toISOString().slice(0, 10),
    menu_name: menu.menu_name,
    menu_category_id: menu.menu_category_id ?? '',
    unit_price: menu.unit_price,
    tax_rate: menu.tax_rate,
    is_main_menu: menu.is_main_menu,
    active_flg: menu.active_flg,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      effective_from: form.effective_from,
      menu_name: form.menu_name,
      menu_category_id: form.menu_category_id || null,
      unit_price: form.unit_price,
      tax_rate: form.tax_rate,
      is_main_menu: form.is_main_menu,
      active_flg: form.active_flg,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white rounded-lg p-6 w-[600px] max-w-full max-h-[90vh] overflow-y-auto space-y-3">
        <div>
          <h2 className="text-h3 flex items-center gap-2">
            <RefreshCw size={20} className="text-brand-orange" />
            メニュー価格改定
          </h2>
          <p className="text-xs text-neutral-sub mt-1">
            指定日から改定後の内容を作成し、現在の内容は前日でクローズされます。過去の売上データは現在の内容に紐付いたまま保持されます。
          </p>
        </div>

        {/* 現在の内容情報 */}
        <div className="bg-neutral-panel rounded p-3 text-xs space-y-0.5">
          <div className="font-medium">現在の内容</div>
          <div>店舗: {storeName}</div>
          <div>コード: {menu.menu_code}</div>
          <div>名称: {menu.menu_name}</div>
          <div>単価: {formatCurrency(menu.unit_price)}（税率{(menu.tax_rate * 100).toFixed(0)}%）</div>
          <div>主要メニュー: {menu.is_main_menu ? '◯' : '×'}</div>
          <div>有効開始: {menu.effective_from}</div>
        </div>

        <div>
          <label className="block text-sm text-neutral-sub mb-1">改定日*（この日から改定後の内容が適用されます）</label>
          <input required type="date" value={form.effective_from}
            min={menu.effective_from}
            max={menu.effective_to ?? undefined}
            onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
            className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
          <p className="text-xs text-neutral-sub mt-1">
            現在の内容は {form.effective_from ? subOneDay(form.effective_from) : '（改定日の前日）'} で終了となります
            {menu.effective_to && <>／既存の有効終了日（{menu.effective_to}）を超える日付は指定できません</>}
          </p>
        </div>

        <div className="pt-2 border-t border-neutral-line">
          <div className="font-medium text-sm mb-2">改定後の内容</div>
          <div>
            <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.menu_name', 'メニュー名')}</label>
            <input value={form.menu_name}
              onChange={(e) => setForm({ ...form, menu_name: e.target.value })}
              className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.menu_category_id', 'カテゴリ')}</label>
              <select value={form.menu_category_id}
                onChange={(e) => setForm({ ...form, menu_category_id: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                {categories.map((c) => <option key={c.menu_category_id} value={c.menu_category_id}>{c.category_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.unit_price', '単価')}(税込)</label>
              <input type="number" min={1} value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: Number(e.target.value) })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.menus.tax_rate', '税率')}</label>
              <input type="number" step="0.01" min={0} max={1} value={form.tax_rate}
                onChange={(e) => setForm({ ...form, tax_rate: Number(e.target.value) })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_main_menu}
                onChange={(e) => setForm({ ...form, is_main_menu: e.target.checked })} />
              {t('field.menus.is_main_menu', '主要メニュー')}（来客数計上）
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active_flg}
                onChange={(e) => setForm({ ...form, active_flg: e.target.checked })} />
              {t('field.menus.active_flg', '有効')}
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-line">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
          <button type="submit" disabled={isPending}
            className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
            {isPending ? '改定中…' : '改定を実行'}
          </button>
        </div>
      </form>
    </div>
  );
}
